import React, { useState } from "react";
import {
  Snackbar,
  Alert,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Box,
} from "@mui/material";
import Sidebar from "../components/Sidebar";
import SnippetEditor from "../components/SnippetEditor";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { logout } from "../redux/authSlice";

const AddSnippet = () => {
  const user = useSelector((state) => state.auth.user);
  const darkMode = useSelector((state) => state.darkMode.isDarkMode);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const dispatch = useDispatch();

  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? "dark" : "light",
          primary: {
            main: darkMode ? "#90caf9" : "#1976d2",
          },
          background: {
            default: darkMode ? "#121212" : "#f5f5f5",
            paper: darkMode ? "#1e1e1e" : "#ffffff",
          },
        },
      }),
    [darkMode]
  );

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSubmit = async (snippetData) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/snippets/add-snippet`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
          body: JSON.stringify(snippetData),
        }
      );

      const data = await response.json();

      // console.log("Response data:", data);

      if (data.message === 'Error creating snippet') {
        throw new Error(data.message || "Failed to save snippet");
      }

      if (data.message === "Invalid token") {
        // Remove user from local storage
        dispatch(logout());
        return;
      }

      // Show success message
      setSnackbar({
        open: true,
        message: "Snippet saved successfully!",
        severity: "success",
      });

      // Wait for snackbar to be visible before redirecting
      setTimeout(() => {
        navigate("/my-snippets");
      }, 1500);
    } catch (error) {
      // console.error("Error saving snippet:", error.message);
      setSnackbar({
        open: true,
        message: `Error: ${error.message}`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          display: "flex",
          minHeight: "100vh",
          bgcolor: "background.default",
        }}
      >
        <Sidebar />
        <Box component="main" sx={{ flexGrow: 1 }}>
          <SnippetEditor
            handleSubmit={handleSubmit}
            title="Create New Snippet"
            loading={loading}
          />
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
};

export default AddSnippet;
